/**
 * OsSessionGuard — OS-INV-10 / MADE-292
 *
 * Wraps PIN-gated mobile routes. If the device has no valid session token
 * (missing or 1-day expiry lapsed) it redirects to /os/pin?next=<current path>
 * before rendering anything.
 *
 * MADE-292: Renders OsCountModeBanner at the top when a count session is active
 * in localStorage. This covers all PIN-gated pages (OsScanEntry, OsScanItem)
 * without per-component wiring. Public count-mode routes (OsCountModeReady)
 * render the banner directly since they bypass this guard.
 *
 * This is a client-side gate only; the real enforcement is server-side
 * (x-os-session-token header validation on every API call). The guard prevents
 * a UX dead-end: the worker lands on a page, the first API call 401s, then we
 * redirect — instead we skip that round-trip and go straight to PIN entry.
 *
 * The `next` redirect param is restricted to /os/* paths to prevent open redirect.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOsSession } from '../hooks/useOsSession';
import { useCountMode } from '../hooks/useCountMode';
import { OsCountModeBanner } from './OsCountModeBanner';

interface OsSessionGuardProps {
  children: React.ReactNode;
}

function sanitizeNext(path: string): string {
  // Only allow /os/* paths; fall back to /os/inventory
  return path.startsWith('/os/') ? path : '/os/inventory';
}

export const OsSessionGuard: React.FC<OsSessionGuardProps> = ({ children }) => {
  const { getSnapshot } = useOsSession();
  const { getCountMode } = useCountMode();
  const location = useLocation();

  const { isAuthenticated } = getSnapshot();

  if (!isAuthenticated) {
    const next = sanitizeNext(location.pathname + location.search);
    return <Navigate to={`/os/pin?next=${encodeURIComponent(next)}`} replace />;
  }

  const countMode = getCountMode();

  return (
    <>
      {countMode && <OsCountModeBanner />}
      {children}
    </>
  );
};
